import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Eye, EyeOff, Link2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { TooltipIconButton } from '../ui/tooltip-icon-button';
import ConfirmDialog from '../ui/confirm-dialog';
import {
  CONDITION_OPERATORS,
  FIELD_TYPES,
  OPERATORS_WITHOUT_VALUE,
  generateFieldId,
  getConditionSummary,
  getPriorFields,
  getVisibleOptions,
  isFieldVisible,
  normalizeField,
  normalizeOption,
} from '../../lib/invitation';
import { readImageAsCompressedDataUrl } from '../../lib/images';
import { cn } from '../../lib/utils';

function ImageUpload({ label, hint, value, onChange, previewMaxH = 120 }) {
  const inputRef = useRef(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const handleFile = async (file) => {
    if (!file) {
      onChange('');
      return;
    }
    try {
      const dataUrl = await readImageAsCompressedDataUrl(file);
      onChange(dataUrl);
    } catch {
      onChange('');
    }
  };

  return (
    <div>
      <label className="ba-label">{label}</label>
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFile(e.dataTransfer.files[0]);
        }}
        className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[var(--ba-input-border)] bg-black/[0.02] px-4 py-6 transition-colors hover:border-brand-orange dark:bg-white/[0.02]"
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {value ? (
          <img src={value} alt="Anteprima" style={{ maxHeight: previewMaxH }} className="max-w-full object-contain" />
        ) : (
          <p className="text-center text-sm text-[var(--ba-muted)]">
            Trascina un&apos;immagine o clicca per selezionare
          </p>
        )}
      </div>
      {value && (
        <TooltipIconButton
          label="Rimuovi immagine"
          icon={Trash2}
          variant="outline"
          className="mt-2"
          onClick={() => setConfirmRemove(true)}
        />
      )}
      {hint && <p className="mt-1 ba-muted">{hint}</p>}

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Rimuovere l'immagine?"
        description="L'immagine verrà eliminata dal modulo."
        confirmLabel="Rimuovi"
        variant="destructive"
        onConfirm={() => onChange('')}
      />
    </div>
  );
}

function createEmptyField() {
  return normalizeField({
    id: generateFieldId(),
    name: '',
    type: 'text',
    options: [],
    multiple: false,
    required: true,
    hint: '',
    hidden: false,
  });
}

function createEmptySection() {
  return normalizeField({
    id: generateFieldId(),
    name: '',
    type: 'section',
    fields: [],
    hidden: false,
  });
}

function ensureFieldId(field) {
  const normalized = normalizeField(field);
  if (normalized.id) return normalized;
  return { ...normalized, id: generateFieldId() };
}

function VisibilityEditor({ field, allFields, onChange }) {
  const priorFields = getPriorFields(allFields, field.id).filter((f) => f.type !== 'section');
  const showWhen = field.showWhen;
  const enabled = !!showWhen?.field;
  const selectedRef = priorFields.find((f) => f.name === showWhen?.field);
  const needsValue = showWhen?.operator && !OPERATORS_WITHOUT_VALUE.includes(showWhen.operator);

  const setEnabled = (nextEnabled) => {
    if (!nextEnabled) {
      onChange(null);
      return;
    }
    const first = priorFields[0];
    onChange({
      field: first?.name || '',
      operator: 'equals',
      value: '',
    });
  };

  const updateShowWhen = (patch) => {
    onChange({ ...(showWhen || { field: '', operator: 'equals', value: '' }), ...patch });
  };

  if (priorFields.length === 0) {
    return (
      <p className="text-sm ba-muted">
        Aggiungi almeno un campo sopra per impostare regole di visibilità.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 accent-brand-orange"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Mostra solo in base a un altro campo
      </label>

      {enabled && (
        <div className="grid gap-2 rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] p-3 dark:bg-white/[0.02] md:grid-cols-[1fr_auto_1fr]">
          <div>
            <label className="ba-label text-xs">Campo di riferimento</label>
            <select
              className="ba-input"
              value={showWhen?.field || ''}
              onChange={(e) => {
                const ref = priorFields.find((f) => f.name === e.target.value);
                const next = { field: e.target.value, operator: showWhen?.operator || 'equals', value: '' };
                if (ref?.type === 'checkbox') next.operator = 'is_checked';
                updateShowWhen(next);
              }}
            >
              <option value="">— seleziona —</option>
              {priorFields.map((f) => (
                <option key={f.id || f.name} value={f.name}>{f.name || 'Senza nome'}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="ba-label text-xs">Condizione</label>
            <select
              className="ba-input"
              value={showWhen?.operator || 'equals'}
              onChange={(e) => updateShowWhen({
                operator: e.target.value,
                value: OPERATORS_WITHOUT_VALUE.includes(e.target.value) ? '' : (showWhen?.value || ''),
              })}
            >
              {CONDITION_OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>

          {needsValue && (
            <div>
              <label className="ba-label text-xs">Valore</label>
              {selectedRef?.type === 'select' && !selectedRef.multiple ? (
                <select
                  className="ba-input"
                  value={showWhen?.value || ''}
                  onChange={(e) => updateShowWhen({ value: e.target.value })}
                >
                  <option value="">— seleziona —</option>
                  {(selectedRef.options || []).map((opt) => (
                    <option key={opt.label} value={opt.label}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <Input
                  value={showWhen?.value || ''}
                  onChange={(e) => updateShowWhen({ value: e.target.value })}
                  placeholder="Valore atteso"
                />
              )}
            </div>
          )}
        </div>
      )}

      {enabled && showWhen?.field && (
        <p className="text-xs ba-muted">
          Visibile {getConditionSummary(showWhen)}
        </p>
      )}
    </div>
  );
}

function FieldCard({
  field,
  index,
  fields,
  rootFields,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
  nested = false,
  structureLocked = false,
}) {
  const [visibilityOpen, setVisibilityOpen] = useState(!!field.showWhen?.field);
  const isSection = field.type === 'section';
  const isHidden = !!field.hidden;

  const updateField = (key, value) => {
    if (structureLocked && key !== 'hidden' && key !== 'fields') return;
    const updated = fields.map((f, i) => {
      if (i !== index) return f;
      const current = ensureFieldId(f);
      if (key === 'type') {
        const next = { ...current, type: value };
        if (value === 'select') {
          next.options = [{ label: '', maxCount: null, hidden: false }];
          next.multiple = false;
        } else {
          next.options = [];
          next.multiple = false;
        }
        return next;
      }
      return { ...current, [key]: value };
    });
    onChange(updated);
  };

  const updateOption = (optionIndex, key, value) => {
    if (structureLocked && key !== 'hidden') return;
    const updated = fields.map((f, i) => {
      if (i !== index) return f;
      const current = ensureFieldId(f);
      const options = [...current.options];
      const currentOpt = normalizeOption(options[optionIndex]);
      options[optionIndex] = { ...currentOpt, ...(typeof options[optionIndex] === 'object' ? options[optionIndex] : {}), [key]: value };
      return { ...current, options };
    });
    onChange(updated);
  };

  const addOption = () => {
    if (structureLocked) return;
    const updated = fields.map((f, i) => {
      if (i !== index) return f;
      const current = ensureFieldId(f);
      return { ...current, options: [...current.options, { label: '', maxCount: null, hidden: false }] };
    });
    onChange(updated);
  };

  const removeOption = (optionIndex) => {
    if (structureLocked) return;
    const updated = fields.map((f, i) => {
      if (i !== index) return f;
      const current = ensureFieldId(f);
      return { ...current, options: current.options.filter((_, oi) => oi !== optionIndex) };
    });
    onChange(updated);
  };

  const updateSectionFields = (sectionFields) => {
    updateField('fields', sectionFields);
  };

  return (
    <div
      className={cn(
        'rounded-xl border p-4',
        isSection ? 'border-brand-orange/30 bg-brand-orange/[0.03]' : 'border-[var(--ba-surface-border)]',
        isHidden && 'border-dashed opacity-75'
      )}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">
            {isSection ? `Sezione ${index + 1}` : `Campo ${index + 1}`}
          </span>
          {isHidden && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--ba-muted)]/15 px-2 py-0.5 text-xs font-medium text-[var(--ba-muted)]">
              <EyeOff className="h-3 w-3" />
              Nascosto dal modulo
            </span>
          )}
          {field.showWhen?.field && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange/15 px-2 py-0.5 text-xs font-medium text-brand-orange">
              <Link2 className="h-3 w-3" />
              Condizionale
            </span>
          )}
        </div>
        {!structureLocked && (
          <div className="flex gap-1">
            <TooltipIconButton label="Sposta su" icon={ChevronUp} variant="outline" onClick={() => onMove(index, -1)} disabled={isFirst} />
            <TooltipIconButton label="Sposta giù" icon={ChevronDown} variant="outline" onClick={() => onMove(index, 1)} disabled={isLast} />
            <TooltipIconButton label={isSection ? 'Elimina sezione' : 'Elimina campo'} icon={Trash2} variant="destructive" onClick={() => onRemove(index)} />
          </div>
        )}
      </div>

      <label className="mb-3 flex cursor-pointer items-start gap-2 rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2.5 text-sm dark:bg-white/[0.02]">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-brand-orange"
          checked={isHidden}
          onChange={(e) => updateField('hidden', e.target.checked)}
        />
        <span>
          <span className="font-medium">Nascondi dal modulo pubblico</span>
          <span className="mt-0.5 block text-xs ba-muted">
            Non viene mostrato ai partecipanti. Le risposte già raccolte restano disponibili in elenco e export.
          </span>
        </span>
      </label>

      {isSection ? (
        <div className="mb-3">
          <label className="ba-label">Titolo sezione</label>
          <Input
            value={field.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="Es. Dettagli volontario"
            disabled={structureLocked}
          />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="ba-label">Tipo</label>
            <select
              className="ba-input"
              value={field.type}
              onChange={(e) => updateField('type', e.target.value)}
              disabled={structureLocked}
            >
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="ba-label">Nome campo</label>
            <Input
              value={field.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
              disabled={structureLocked}
            />
          </div>
        </div>
      )}

      {!isSection && (
        <label className={cn('mt-3 flex items-center gap-2 text-sm', structureLocked && 'opacity-70')}>
          <input
            type="checkbox"
            className="h-4 w-4 accent-brand-orange"
            checked={field.required}
            onChange={(e) => updateField('required', e.target.checked)}
            disabled={structureLocked}
          />
          Campo obbligatorio
        </label>
      )}

      {!isSection && (
        <div className="mt-3">
          <label className="ba-label">Testo di aiuto (opzionale)</label>
          <Input
            value={field.hint || ''}
            onChange={(e) => updateField('hint', e.target.value)}
            placeholder="Es. Indica un contatto raggiungibile durante l'evento"
            disabled={structureLocked}
          />
        </div>
      )}

      {!isSection && field.type === 'select' && (
        <div className="mt-3 space-y-2">
          <label className={cn('flex items-center gap-2 text-sm', structureLocked && 'opacity-70')}>
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-orange"
              checked={field.multiple}
              onChange={(e) => updateField('multiple', e.target.checked)}
              disabled={structureLocked}
            />
            Consenti scelta multipla
          </label>
          <label className="ba-label">Opzioni</label>
          <p className="text-sm ba-muted">
            {structureLocked
              ? 'Puoi nascondere singole opzioni dal modulo pubblico. Le risposte già raccolte restano disponibili.'
              : 'Il campo numerico accanto a ogni opzione indica quante volte può essere selezionata. Lascialo vuoto se non ci sono limiti. Puoi anche nascondere un\'opzione senza eliminarla.'}
          </p>
          {field.options.map((rawOpt, optIdx) => {
            const opt = normalizeOption(rawOpt);
            return (
              <div
                key={optIdx}
                className={cn(
                  'space-y-2 rounded-lg border border-[var(--ba-surface-border)] p-2.5',
                  opt.hidden && 'border-dashed opacity-75'
                )}
              >
                <div className="flex flex-wrap gap-2">
                  <Input
                    className="min-w-[140px] flex-1"
                    placeholder="Etichetta opzione"
                    value={opt.label}
                    onChange={(e) => updateOption(optIdx, 'label', e.target.value)}
                    required
                    disabled={structureLocked}
                  />
                  <Input
                    type="number"
                    min="1"
                    className="w-28"
                    placeholder="Max posti"
                    value={opt.maxCount ?? ''}
                    onChange={(e) => updateOption(optIdx, 'maxCount', e.target.value ? Number(e.target.value) : null)}
                    disabled={structureLocked}
                  />
                  {!structureLocked && (
                    <TooltipIconButton
                      label="Rimuovi opzione"
                      icon={Trash2}
                      variant="destructive"
                      onClick={() => removeOption(optIdx)}
                      disabled={field.options.length <= 1}
                    />
                  )}
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-orange"
                    checked={!!opt.hidden}
                    onChange={(e) => updateOption(optIdx, 'hidden', e.target.checked)}
                  />
                  <span className="inline-flex items-center gap-1.5">
                    <EyeOff className="h-3.5 w-3.5 text-[var(--ba-muted)]" />
                    Nascondi dal modulo pubblico
                  </span>
                </label>
              </div>
            );
          })}
          {!structureLocked && (
            <Button type="button" variant="outline" onClick={addOption}>
              + Aggiungi opzione
            </Button>
          )}
        </div>
      )}

      {!structureLocked && (
        <details
          className="mt-4 rounded-lg border border-[var(--ba-surface-border)]"
          open={visibilityOpen}
          onToggle={(e) => setVisibilityOpen(e.target.open)}
        >
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
            Visibilità {field.showWhen?.field ? '(condizionale)' : '(sempre visibile)'}
          </summary>
          <div className="border-t border-[var(--ba-surface-border)] px-3 py-3">
            <VisibilityEditor
              field={field}
              allFields={rootFields}
              onChange={(showWhen) => updateField('showWhen', showWhen)}
            />
          </div>
        </details>
      )}

      {structureLocked && field.showWhen?.field && (
        <p className="mt-3 text-xs ba-muted">
          Condizione esistente: visibile {getConditionSummary(field.showWhen)}
        </p>
      )}

      {isSection && (
        <div className="mt-4 rounded-lg border border-dashed border-[var(--ba-surface-border)] p-3">
          <p className="mb-3 text-sm font-medium">Campi nella sezione</p>
          <FieldBuilder
            fields={field.fields || []}
            onChange={updateSectionFields}
            rootFields={rootFields}
            nested
            structureLocked={structureLocked}
          />
        </div>
      )}
    </div>
  );
}

function FieldBuilder({ fields, onChange, nested = false, rootFields, structureLocked = false }) {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewValues, setPreviewValues] = useState({});

  const normalizedFields = fields.map(ensureFieldId);
  const tree = rootFields || normalizedFields;

  const addField = () => {
    if (structureLocked) return;
    onChange([...normalizedFields, createEmptyField()]);
  };

  const addSection = () => {
    if (structureLocked) return;
    onChange([...normalizedFields, createEmptySection()]);
  };

  const removeField = (index) => {
    if (structureLocked) return;
    onChange(normalizedFields.filter((_, i) => i !== index));
  };

  const moveField = (index, direction) => {
    if (structureLocked) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= normalizedFields.length) return;
    const updated = [...normalizedFields];
    const [moved] = updated.splice(index, 1);
    updated.splice(newIndex, 0, moved);
    onChange(updated);
  };

  const confirmDeleteProps = () => {
    if (!pendingDelete) return null;
    const field = normalizeField(normalizedFields[pendingDelete.index]);
    const label = field.type === 'section' ? 'sezione' : 'campo';
    const name = field.name || `${label} ${pendingDelete.index + 1}`;
    return {
      title: field.type === 'section' ? 'Eliminare la sezione?' : 'Eliminare il campo?',
      description: `La ${label} "${name}" verrà rimossa dal modulo${field.type === 'section' ? ' insieme ai campi al suo interno' : ''}.`,
      onConfirm: () => {
        const { index } = pendingDelete;
        setPendingDelete(null);
        removeField(index);
      },
    };
  };

  const deleteDialog = confirmDeleteProps();

  const renderPreviewItem = (rawField, idx, parentVisible = true) => {
    const field = normalizeField(rawField);
    const visible = parentVisible && isFieldVisible(field, previewValues);

    if (field.type === 'section') {
      const sectionVisible = parentVisible && isFieldVisible(field, previewValues);
      if (!sectionVisible) return null;
      return (
        <div key={field.id || idx} className="rounded-lg border border-brand-orange/20 bg-brand-orange/[0.04] p-3">
          {field.name && <p className="mb-2 text-sm font-semibold">{field.name}</p>}
          <div className="space-y-2">
            {(field.fields || []).map((child, childIdx) => renderPreviewItem(child, childIdx, sectionVisible))}
          </div>
        </div>
      );
    }

    if (!visible) return null;

    return (
      <div key={field.id || idx} className="space-y-1">
        {field.type === 'checkbox' ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-orange"
              checked={!!previewValues[field.name]}
              onChange={(e) => setPreviewValues((prev) => ({ ...prev, [field.name]: e.target.checked }))}
            />
            {field.name}
          </label>
        ) : field.type === 'select' && field.multiple ? (
          <>
            <label className="ba-label text-xs">{field.name}</label>
            <div className="space-y-1">
              {getVisibleOptions(field.options).map((opt) => (
                <label key={opt.label} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-orange"
                    checked={(previewValues[field.name] || []).includes(opt.label)}
                    onChange={(e) => {
                      const current = Array.isArray(previewValues[field.name]) ? previewValues[field.name] : [];
                      const next = e.target.checked
                        ? [...current, opt.label]
                        : current.filter((v) => v !== opt.label);
                      setPreviewValues((prev) => ({ ...prev, [field.name]: next }));
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </>
        ) : field.type === 'select' ? (
          <>
            <label className="ba-label text-xs">{field.name}</label>
            <select
              className="ba-input"
              value={previewValues[field.name] || ''}
              onChange={(e) => setPreviewValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
            >
              <option value="">— seleziona —</option>
              {getVisibleOptions(field.options).map((opt) => (
                <option key={opt.label} value={opt.label}>{opt.label}</option>
              ))}
            </select>
          </>
        ) : (
          <>
            <label className="ba-label text-xs">{field.name}</label>
            <input
              className="ba-input"
              value={previewValues[field.name] || ''}
              onChange={(e) => setPreviewValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {!nested && (
        <div className="rounded-xl border border-[var(--ba-surface-border)] bg-black/[0.02] p-4 dark:bg-white/[0.02]">
          <button
            type="button"
            className="flex w-full items-center justify-between text-left text-sm font-medium"
            onClick={() => setPreviewOpen((v) => !v)}
          >
            <span className="inline-flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Anteprima logica condizionale
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${previewOpen ? 'rotate-180' : ''}`} />
          </button>
          {previewOpen && (
            <div className="mt-3 space-y-3 border-t border-[var(--ba-surface-border)] pt-3">
              <p className="text-sm ba-muted">
                Compila i campi qui sotto per verificare quali sezioni e campi condizionali vengono mostrati.
                I campi nascosti dal modulo non compaiono qui.
              </p>
              <div className="space-y-3">
                {normalizedFields.length === 0 ? (
                  <p className="text-sm ba-muted">Aggiungi campi per iniziare l&apos;anteprima.</p>
                ) : (
                  normalizedFields.map((field, idx) => renderPreviewItem(field, idx))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {normalizedFields.map((field, index) => (
        <FieldCard
          key={field.id || index}
          field={field}
          index={index}
          fields={normalizedFields}
          rootFields={tree}
          onChange={onChange}
          onRemove={(idx) => setPendingDelete({ index: idx })}
          onMove={moveField}
          isFirst={index === 0}
          isLast={index === normalizedFields.length - 1}
          nested={nested}
          structureLocked={structureLocked}
        />
      ))}

      {!structureLocked && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={addField}>
            <Plus className="h-4 w-4" />
            Aggiungi campo
          </Button>
          {!nested && (
            <Button type="button" variant="outline" onClick={addSection}>
              <Plus className="h-4 w-4" />
              Aggiungi sezione
            </Button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={deleteDialog?.title}
        description={deleteDialog?.description}
        confirmLabel="Elimina"
        variant="destructive"
        onConfirm={deleteDialog?.onConfirm}
      />
    </div>
  );
}

export { ImageUpload, FieldBuilder };
